import {
  CARE_SOURCE_CONTEXTS,
  CARE_SOURCE_PROCESSING_STATUSES,
  CARE_SOURCE_REVIEW_STATES,
  CARE_SOURCE_TEXT_EXTRACTION_STATUSES,
  buildCareSourceFileMetadata,
} from '../features/careSourceProcessing/careSourceMetadata';
import { CARE_SOURCE_TYPES } from '../features/careSourceProcessing/sourceTypes';
import { applyImageDisplayName } from '../features/imageCapture/imageDisplayName';
import { IMAGE_CAPTURE_INTENTS, normalizeImageCaptureIntent } from '../features/imageCapture/imageCaptureIntent';
import { getCalendarDateKey } from './calendarDateKey';

const buildUploadPath = ({ childId = '', uploadId = '' } = {}) => (
  `careSources/quickLogPhotos/${childId || 'unknown-child'}/${uploadId}`
);

const toSafeObjectName = (value = '') => String(value || 'photo')
  .replace(/[^A-Za-z0-9._-]+/g, '_')
  .slice(-180);

const createPhotoSaveError = ({ stage, error }) => {
  const wrappedError = new Error(
    stage === 'upload'
      ? 'The photo could not be uploaded.'
      : 'The uploaded photo could not be saved to Lifelog.'
  );
  wrappedError.code = error?.code || `photo-${stage}-failed`;
  wrappedError.photoSaveStage = stage;
  wrappedError.cause = error;
  return wrappedError;
};

export const saveQuickLogPhotoSource = async ({
  child = null,
  photoFile = null,
  user = null,
  selectedDate = null,
  selectedTime = '',
  caregiverContext = '',
  photoIntent = IMAGE_CAPTURE_INTENTS.READ_TEXT,
  clientRequestId = '',
  uploadIncidentMedia,
  captureCareSourceForProcessing,
} = {}) => {
  if (!child?.id) {
    throw new Error('Child is required before saving a photo note.');
  }
  if (!user?.uid) {
    throw new Error('You must be signed in before saving a photo note.');
  }
  if (!photoFile) {
    throw new Error('Choose a photo before saving.');
  }
  if (typeof uploadIncidentMedia !== 'function' || typeof captureCareSourceForProcessing !== 'function') {
    throw new Error('Photo source save dependencies are missing.');
  }

  const normalizedIntent = normalizeImageCaptureIntent(photoIntent);
  const normalizedRequestId = String(clientRequestId || `photo_${Date.now()}`).trim();
  const caregiverText = String(caregiverContext || '').trim();
  const isKeepPhoto = normalizedIntent === IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
  const uploadId = `quick-log-photo-${normalizedRequestId}`;
  let uploadedMedia;
  try {
    uploadedMedia = await uploadIncidentMedia(
      { file: photoFile, type: 'image' },
      null,
      uploadId,
      buildUploadPath({ childId: child.id, uploadId }),
      { imageObjectName: `${toSafeObjectName(normalizedRequestId)}_${toSafeObjectName(photoFile.name)}` }
    );
  } catch (error) {
    throw createPhotoSaveError({ stage: 'upload', error });
  }
  const imageAttachments = applyImageDisplayName({
    attachments: Array.isArray(uploadedMedia) ? uploadedMedia : [],
    date: selectedDate,
    time: selectedTime,
    originalFileName: photoFile.name,
  });
  const sourceMetadata = {
    ...buildCareSourceFileMetadata({
      file: photoFile,
      sourceContext: isKeepPhoto && !caregiverText
        ? CARE_SOURCE_CONTEXTS.UNKNOWN
        : CARE_SOURCE_CONTEXTS.TIMELINE,
      textExtractionStatus: isKeepPhoto
        ? CARE_SOURCE_TEXT_EXTRACTION_STATUSES.NOT_NEEDED
        : CARE_SOURCE_TEXT_EXTRACTION_STATUSES.PENDING,
      extractionError: isKeepPhoto ? null : {
        code: 'ocr-not-ready',
        message: 'Image text extraction is not available yet.',
      },
      textLength: isKeepPhoto ? caregiverText.length : 0,
    }),
    processingStatus: CARE_SOURCE_PROCESSING_STATUSES.SAVED_SOURCE,
    reviewState: CARE_SOURCE_REVIEW_STATES.NOT_NEEDED,
    sourceName: isKeepPhoto ? 'Photo' : 'Photo of a note',
    inputMode: 'quick_log_photo',
    photoIntent: normalizedIntent,
    selectedDate: selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
      ? selectedDate.toISOString()
      : null,
    selectedLocalDate: selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
      ? getCalendarDateKey(selectedDate)
      : null,
    selectedTime: selectedTime || '',
    caregiverContext: caregiverText,
    imageAttachments,
    mediaUrls: imageAttachments.map((item) => item.url).filter(Boolean),
  };

  try {
    return await captureCareSourceForProcessing({
      childId: child.id,
      rawText: isKeepPhoto ? caregiverText : '',
      sourceType: CARE_SOURCE_TYPES.SCREENSHOT,
      clientRequestId: normalizedRequestId,
      sourceMetadata,
    });
  } catch (error) {
    throw createPhotoSaveError({ stage: 'source', error });
  }
};
